import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { 
  Building2, MapPin, Search, FileText, Settings, LogOut, 
  BarChart3, TrendingUp, Home, Bed, Bath, Square, DollarSign,
  Loader2, ChevronRight, Clock, Crown, Calculator, HelpCircle,
  Menu, X, Link as LinkIcon, ExternalLink, Upload, FileSpreadsheet
} from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png";

const DashboardPage = ({ auth }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [recentReports, setRecentReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Analysis form state
  const [analysisType, setAnalysisType] = useState("address");
  const [address, setAddress] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [propertyType, setPropertyType] = useState("single_family");
  const [bedrooms, setBedrooms] = useState("3");
  const [bathrooms, setBathrooms] = useState("2");
  const [sqft, setSqft] = useState("");
  const [currentRent, setCurrentRent] = useState("");
  const [radius, setRadius] = useState("1.0");

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const autocompleteRef = useRef(null);
  const debounceTimer = useRef(null);

  // Zillow import state
  const [zillowUrl, setZillowUrl] = useState("");
  const [loadingZillow, setLoadingZillow] = useState(false);
  const [importedProperty, setImportedProperty] = useState(null);

  // Bulk import state
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const fileInputRef = useRef(null);

  // Market trends state
  const [marketTrends, setMarketTrends] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [trendsZipCode, setTrendsZipCode] = useState("");

  useEffect(() => {
    fetchRecentReports();
    
    // Click outside to close suggestions
    const handleClickOutside = (e) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchRecentReports = async () => {
    try {
      const response = await axios.get(`${API}/reports?limit=5`, {
        withCredentials: true
      });
      setRecentReports(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/address/autocomplete?q=${encodeURIComponent(query)}&limit=5`);
      setSuggestions(response.data);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Autocomplete error:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddress(value);

    // Debounce autocomplete requests
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const selectSuggestion = (suggestion) => {
    setAddress(suggestion.display_name || suggestion.address);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Import from Zillow
  const handleZillowImport = async () => {
    if (!zillowUrl.trim()) {
      toast.error("Please enter a Zillow URL");
      return;
    }

    if (!zillowUrl.toLowerCase().includes("zillow.com")) {
      toast.error("Please enter a valid Zillow URL");
      return;
    }

    setLoadingZillow(true);
    try {
      const response = await axios.post(`${API}/scrape/zillow`, {
        url: zillowUrl.trim()
      }, { withCredentials: true });

      const data = response.data;
      
      // Store imported property data
      setImportedProperty(data);
      
      // Auto-fill the analysis form
      if (data.address) {
        const fullAddress = [data.address, data.city, data.state, data.zip_code].filter(Boolean).join(", ");
        setAddress(fullAddress);
        setAnalysisType("address");
      }
      if (data.property_type) setPropertyType(data.property_type);
      if (data.beds) setBedrooms(String(data.beds));
      if (data.baths) setBathrooms(String(data.baths));
      if (data.sqft) setSqft(String(data.sqft));
      
      // Store in localStorage for calculators to use
      localStorage.setItem("zillow_imported_property", JSON.stringify({
        ...data,
        imported_at: new Date().toISOString()
      }));

      toast.success(`Property imported: ${data.address || "Unknown address"}`, {
        description: `${data.beds || 0} bed, ${data.baths || 0} bath${data.price ? ` • $${data.price.toLocaleString()}` : ""}`,
        action: {
          label: "Go to Calculators",
          onClick: () => navigate("/calculators")
        }
      });

      setZillowUrl("");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to import from Zillow";
      toast.error(message);
    } finally {
      setLoadingZillow(false);
    }
  };

  // Clear imported property
  const clearImportedProperty = () => {
    setImportedProperty(null);
    localStorage.removeItem("zillow_imported_property");
  };

  // Bulk import from CSV
  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }

    setBulkImporting(true);
    setBulkImportFile(file);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
      
      // Find column indices
      const addressIdx = headers.findIndex(h => h.includes('address'));
      const cityIdx = headers.findIndex(h => h.includes('city'));
      const stateIdx = headers.findIndex(h => h.includes('state'));
      const zipIdx = headers.findIndex(h => h.includes('zip'));
      const bedsIdx = headers.findIndex(h => h.includes('bed'));
      const bathsIdx = headers.findIndex(h => h.includes('bath'));
      const priceIdx = headers.findIndex(h => h.includes('price') || h.includes('list'));
      const sqftIdx = headers.findIndex(h => h.includes('sqft') || h.includes('sq ft') || h.includes('square'));

      const properties = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        if (values.length >= 2) {
          properties.push({
            address: addressIdx >= 0 ? values[addressIdx] : '',
            city: cityIdx >= 0 ? values[cityIdx] : '',
            state: stateIdx >= 0 ? values[stateIdx] : '',
            zip_code: zipIdx >= 0 ? values[zipIdx] : '',
            beds: bedsIdx >= 0 ? parseInt(values[bedsIdx]) || 0 : 0,
            baths: bathsIdx >= 0 ? parseFloat(values[bathsIdx]) || 0 : 0,
            price: priceIdx >= 0 ? parseInt(values[priceIdx].replace(/[^0-9]/g, '')) || 0 : 0,
            sqft: sqftIdx >= 0 ? parseInt(values[sqftIdx].replace(/[^0-9]/g, '')) || 0 : 0,
          });
        }
      }

      setBulkImportResults({
        total: properties.length,
        properties: properties.slice(0, 10), // Show first 10
        hasMore: properties.length > 10
      });

      // Store all properties for calculators
      localStorage.setItem("bulk_imported_properties", JSON.stringify({
        properties,
        imported_at: new Date().toISOString()
      }));

      toast.success(`Imported ${properties.length} properties from CSV`);
    } catch (error) {
      toast.error("Failed to parse CSV file");
      console.error(error);
    } finally {
      setBulkImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Clear bulk import
  const clearBulkImport = () => {
    setBulkImportResults(null);
    setBulkImportFile(null);
    localStorage.removeItem("bulk_imported_properties");
  };

  // Fetch market trends
  const fetchMarketTrends = async (zip) => {
    const targetZip = zip || trendsZipCode || zipCode;
    if (!targetZip || targetZip.length < 5) {
      toast.error("Please enter a valid ZIP code");
      return;
    }

    setLoadingTrends(true);
    try {
      const response = await axios.post(`${API}/market/statistics`, {
        zip_code: targetZip,
        bedrooms: parseInt(bedrooms) || 3
      }, { withCredentials: true });

      setMarketTrends(response.data);
      setTrendsZipCode(targetZip);
    } catch (error) {
      toast.error("Failed to fetch market trends");
    } finally {
      setLoadingTrends(false);
    }
  };

  const handleAnalysis = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      
      if (analysisType === "address") {
        if (!address.trim()) {
          toast.error("Please enter an address");
          setLoading(false);
          return;
        }
        
        response = await axios.post(`${API}/analysis/address`, {
          address: address.trim(),
          property_type: propertyType,
          bedrooms: parseInt(bedrooms),
          bathrooms: parseFloat(bathrooms),
          sqft: sqft ? parseInt(sqft) : null,
          current_rent: currentRent ? parseFloat(currentRent) : null,
          radius_miles: parseFloat(radius)
        }, { withCredentials: true });
      } else {
        if (!zipCode.trim()) {
          toast.error("Please enter a ZIP code");
          setLoading(false);
          return;
        }
        
        response = await axios.post(`${API}/analysis/zip`, {
          zip_code: zipCode.trim(),
          property_type: propertyType,
          bedrooms: parseInt(bedrooms),
          bathrooms: parseFloat(bathrooms)
        }, { withCredentials: true });
      }

      toast.success("Analysis complete!");
      navigate(`/report/${response.data.report_id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      
      // Handle trial expired - redirect to subscription
      if (error.response?.status === 403 && detail?.redirect) {
        toast.error(detail.message || "Free trial expired. Please subscribe to continue.");
        navigate(detail.redirect);
        return;
      }
      
      const message = typeof detail === 'string' ? detail : detail?.message || "Analysis failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const tierBadgeColor = {
    trial: "bg-slate-100 text-slate-700",
    basic: "bg-teal-100 text-teal-700",
    pro: "bg-purple-100 text-purple-700",
    admin: "bg-amber-100 text-amber-700"
  };

  const currentTier = auth.user?.subscription_tier || "trial";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-navy-900 text-white hidden lg:block">
        <div className="p-6">
          <Link to="/" className="flex items-center space-x-2">
            <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
            
          </Link>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white"
            data-testid="nav-dashboard"
          >
            <BarChart3 className="h-5 w-5" />
            <span>Dashboard</span>
          </Link>
          <Link 
            to="/reports" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="nav-reports"
          >
            <FileText className="h-5 w-5" />
            <span>My Reports</span>
          </Link>
          <Link 
            to="/calculators" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="nav-calculators"
          >
            <Calculator className="h-5 w-5" />
            <span>Deal Calculators</span>
          </Link>
          <Link 
            to="/compare" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="nav-compare"
          >
            <TrendingUp className="h-5 w-5" />
            <span>Compare</span>
            {currentTier !== "pro" && currentTier !== "admin" && (
              <Badge className="bg-purple-500 text-white text-xs ml-auto">PRO</Badge>
            )}
          </Link>
          <Link 
            to="/subscription" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="nav-subscription"
          >
            <Crown className="h-5 w-5" />
            <span>Subscription</span>
          </Link>
          <Link 
            to="/support" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="nav-support"
          >
            <HelpCircle className="h-5 w-5" />
            <span>Support</span>
          </Link>
          <Link 
            to="/settings" 
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="nav-settings"
          >
            <Settings className="h-5 w-5" />
            <span>Settings</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={auth.user?.picture} />
                <AvatarFallback className="bg-teal-500 text-navy-900">
                  {auth.user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                <Badge className={`${tierBadgeColor[currentTier] || tierBadgeColor.basic} text-xs`}>
                  {currentTier.toUpperCase()}
                </Badge>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
              onClick={auth.logout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-navy-900 text-white overflow-y-auto">
            <div className="p-4 flex items-center justify-between border-b border-white/10">
              <Link to="/" className="flex items-center space-x-2">
                <img src={LOGO_URL} alt="Rentlytix" className="h-10 w-auto" />
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className="text-white hover:bg-white/10">
                <X className="h-6 w-6" />
              </Button>
            </div>
            
            <nav className="px-4 py-4 space-y-1">
              <Link to="/dashboard" className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-white/10 text-white" onClick={() => setMobileMenuOpen(false)}>
                <BarChart3 className="h-5 w-5" /><span>Dashboard</span>
              </Link>
              <Link to="/reports" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <FileText className="h-5 w-5" /><span>My Reports</span>
              </Link>
              <Link to="/calculators" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Calculator className="h-5 w-5" /><span>Deal Calculators</span>
              </Link>
              <Link to="/compare" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <TrendingUp className="h-5 w-5" /><span>Compare</span>
                {currentTier !== "pro" && currentTier !== "admin" && <Badge className="bg-purple-500 text-white text-xs ml-auto">PRO</Badge>}
              </Link>
              <Link to="/subscription" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Crown className="h-5 w-5" /><span>Subscription</span>
              </Link>
              <Link to="/support" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <HelpCircle className="h-5 w-5" /><span>Support</span>
              </Link>
              <Link to="/settings" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Settings className="h-5 w-5" /><span>Settings</span>
              </Link>
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
              <div className="flex items-center space-x-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={auth.user?.picture} />
                  <AvatarFallback className="bg-teal-500 text-navy-900">{auth.user?.name?.charAt(0) || "U"}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{auth.user?.name}</p>
                  <Badge className={`${tierBadgeColor[currentTier]} text-xs`}>{currentTier.toUpperCase()}</Badge>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10" onClick={auth.logout}>
                <LogOut className="h-4 w-4 mr-2" />Sign Out
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)}>
              <Menu className="h-6 w-6" />
            </Button>
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Rentlytix" className="h-8 w-auto" />
            </Link>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/reports")}>
              <FileText className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")}>
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 max-w-6xl">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-navy-900 mb-2">
              Welcome back, {auth.user?.name?.split(" ")[0] || "User"}
            </h1>
            <p className="text-slate-600">
              Analyze any property to get instant rent estimates and market insights.
            </p>
          </div>

          {/* Import Properties Section - TOP */}
          <Card className="mb-8 shadow-lg border-0" data-testid="import-section-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center text-lg">
                <Upload className="h-5 w-5 mr-2 text-blue-500" />
                Import Properties
              </CardTitle>
              <CardDescription>
                Import from Zillow or upload a CSV file with multiple properties
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Zillow Import */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <LinkIcon className="h-4 w-4 text-blue-500" />
                    Import from Zillow
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="https://www.zillow.com/homedetails/..."
                        value={zillowUrl}
                        onChange={(e) => setZillowUrl(e.target.value)}
                        className="pl-10 h-10"
                        data-testid="zillow-url-input"
                      />
                    </div>
                    <Button
                      onClick={handleZillowImport}
                      disabled={loadingZillow}
                      className="bg-blue-500 hover:bg-blue-400 text-white h-10"
                      data-testid="zillow-import-btn"
                    >
                      {loadingZillow ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import"}
                    </Button>
                  </div>
                </div>

                {/* Bulk CSV Import */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-green-500" />
                    Bulk Import (CSV)
                  </Label>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleBulkImport}
                      ref={fileInputRef}
                      className="hidden"
                      id="csv-upload"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={bulkImporting}
                      className="flex-1 h-10"
                      data-testid="bulk-import-btn"
                    >
                      {bulkImporting ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      {bulkImportFile ? bulkImportFile.name : "Choose CSV File"}
                    </Button>
                    {bulkImportResults && (
                      <Button variant="ghost" size="icon" onClick={clearBulkImport}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    CSV should have columns: address, city, state, zip, beds, baths, price, sqft
                  </p>
                </div>
              </div>

              {/* Imported Property Display */}
              {importedProperty && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-1">
                        {importedProperty.address || "Property Imported"}
                      </h4>
                      <p className="text-sm text-slate-600">
                        {[importedProperty.city, importedProperty.state, importedProperty.zip_code].filter(Boolean).join(", ")}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm">
                        {importedProperty.price > 0 && (
                          <span className="flex items-center text-green-700">
                            <DollarSign className="h-4 w-4 mr-1" />{importedProperty.price.toLocaleString()}
                          </span>
                        )}
                        {importedProperty.beds > 0 && (
                          <span className="flex items-center text-slate-600">
                            <Bed className="h-4 w-4 mr-1" />{importedProperty.beds} bed
                          </span>
                        )}
                        {importedProperty.baths > 0 && (
                          <span className="flex items-center text-slate-600">
                            <Bath className="h-4 w-4 mr-1" />{importedProperty.baths} bath
                          </span>
                        )}
                        {importedProperty.sqft > 0 && (
                          <span className="flex items-center text-slate-600">
                            <Square className="h-4 w-4 mr-1" />{importedProperty.sqft.toLocaleString()} sqft
                          </span>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={clearImportedProperty} className="text-slate-400 hover:text-slate-600">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <Separator className="my-3" />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigate("/calculators")} className="flex-1">
                      <Calculator className="h-4 w-4 mr-2" />Use in Calculators
                    </Button>
                    <Button size="sm" onClick={handleAnalysis} disabled={loading} className="flex-1 bg-teal-500 hover:bg-teal-400 text-navy-900">
                      <TrendingUp className="h-4 w-4 mr-2" />Run Analysis
                    </Button>
                  </div>
                </div>
              )}

              {/* Bulk Import Results */}
              {bulkImportResults && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-green-800">
                      {bulkImportResults.total} Properties Imported
                    </h4>
                    <Button variant="ghost" size="sm" onClick={clearBulkImport} className="text-slate-400 hover:text-slate-600">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-auto">
                    {bulkImportResults.properties.map((prop, idx) => (
                      <div key={idx} className="text-sm flex items-center justify-between py-1 border-b border-green-100 last:border-0">
                        <span className="text-slate-700">{prop.address || `Property ${idx + 1}`}</span>
                        <span className="text-slate-500">{prop.beds}bd/{prop.baths}ba {prop.price > 0 && `• $${prop.price.toLocaleString()}`}</span>
                      </div>
                    ))}
                    {bulkImportResults.hasMore && (
                      <p className="text-xs text-slate-500 pt-1">...and {bulkImportResults.total - 10} more</p>
                    )}
                  </div>
                  <Button size="sm" onClick={() => navigate("/calculators")} className="w-full mt-3 bg-green-600 hover:bg-green-500 text-white">
                    <Calculator className="h-4 w-4 mr-2" />Analyze in Calculators
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Market Rent Trends */}
          <Card className="mb-8 shadow-lg border-0" data-testid="market-trends-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center text-lg">
                    <BarChart3 className="h-5 w-5 mr-2 text-purple-500" />
                    Market Rent Trends
                  </CardTitle>
                  <CardDescription>
                    View 12-month rental price trends by ZIP code
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="ZIP Code"
                    value={trendsZipCode}
                    onChange={(e) => setTrendsZipCode(e.target.value)}
                    className="w-28 h-9"
                    maxLength={5}
                  />
                  <Button
                    size="sm"
                    onClick={() => fetchMarketTrends()}
                    disabled={loadingTrends}
                    className="bg-purple-500 hover:bg-purple-400 text-white"
                  >
                    {loadingTrends ? <Loader2 className="h-4 w-4 animate-spin" /> : "View Trends"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {marketTrends ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-2xl font-bold text-navy-900">
                        ${marketTrends.current_average?.toLocaleString()}/mo
                      </p>
                      <p className="text-sm text-slate-500">Current average rent in {marketTrends.zip_code}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      marketTrends.yoy_change_percent > 0 
                        ? 'bg-green-100 text-green-700' 
                        : marketTrends.yoy_change_percent < 0 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-slate-100 text-slate-700'
                    }`}>
                      {marketTrends.yoy_change_percent > 0 ? '↑' : marketTrends.yoy_change_percent < 0 ? '↓' : '→'} {Math.abs(marketTrends.yoy_change_percent)}% YoY
                    </div>
                  </div>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={marketTrends.monthly_history} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="rentGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 10 }} 
                          tickFormatter={(val) => val.split('-')[1]}
                          stroke="#94a3b8"
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }} 
                          tickFormatter={(val) => `$${(val/1000).toFixed(1)}k`}
                          stroke="#94a3b8"
                          width={50}
                        />
                        <Tooltip 
                          formatter={(value) => [`$${value.toLocaleString()}`, 'Avg Rent']}
                          labelFormatter={(label) => marketTrends.monthly_history.find(m => m.month === label)?.month_name || label}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="average_rent" 
                          stroke="#8b5cf6" 
                          strokeWidth={2}
                          fill="url(#rentGradient)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  {marketTrends.source === 'simulated' && (
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      * Simulated data for demonstration
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Enter a ZIP code to view rental market trends</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Analysis Form */}
          <Card className="mb-8 shadow-lg border-0" data-testid="analysis-form-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center">
                <Search className="h-5 w-5 mr-2 text-teal-500" />
                New Analysis
              </CardTitle>
              <CardDescription>
                Enter property details to get an AI-powered rent estimate
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={analysisType} onValueChange={setAnalysisType}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="address" data-testid="tab-address">By Address</TabsTrigger>
                  <TabsTrigger value="zip" data-testid="tab-zip">By ZIP Code</TabsTrigger>
                </TabsList>

                <form onSubmit={handleAnalysis}>
                  <TabsContent value="address" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="address">Property Address</Label>
                      <div className="relative" ref={autocompleteRef}>
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                        <Input
                          id="address"
                          placeholder="123 Main St, City, State"
                          value={address}
                          onChange={handleAddressChange}
                          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                          className="pl-10"
                          data-testid="input-address"
                          autoComplete="off"
                        />
                        {loadingSuggestions && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                        )}
                        
                        {/* Autocomplete Dropdown */}
                        {showSuggestions && suggestions.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                            {suggestions.map((suggestion, index) => (
                              <button
                                key={index}
                                type="button"
                                className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-0"
                                onClick={() => selectSuggestion(suggestion)}
                                data-testid={`suggestion-${index}`}
                              >
                                <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="font-medium text-navy-900 truncate">{suggestion.address}</p>
                                  <p className="text-sm text-slate-500 truncate">
                                    {suggestion.city}, {suggestion.state} {suggestion.zip_code}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="zip" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="zipCode">ZIP Code</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="zipCode"
                          placeholder="10001"
                          value={zipCode}
                          onChange={(e) => setZipCode(e.target.value)}
                          className="pl-10"
                          maxLength={5}
                          data-testid="input-zip"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>Property Type</Label>
                      <Select value={propertyType} onValueChange={setPropertyType}>
                        <SelectTrigger data-testid="select-property-type">
                          <Home className="h-4 w-4 mr-2 text-slate-400" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single_family">Single Family</SelectItem>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="condo">Condo</SelectItem>
                          <SelectItem value="townhouse">Townhouse</SelectItem>
                          <SelectItem value="duplex">Duplex</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Bedrooms</Label>
                      <Select value={bedrooms} onValueChange={setBedrooms}>
                        <SelectTrigger data-testid="select-bedrooms">
                          <Bed className="h-4 w-4 mr-2 text-slate-400" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({length: 20}, (_, i) => i + 1).map(n => (
                            <SelectItem key={n} value={String(n)}>{n} BR</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Bathrooms</Label>
                      <Select value={bathrooms} onValueChange={setBathrooms}>
                        <SelectTrigger data-testid="select-bathrooms">
                          <Bath className="h-4 w-4 mr-2 text-slate-400" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({length: 39}, (_, i) => (i + 2) / 2).map(n => (
                            <SelectItem key={n} value={String(n)}>{n} BA</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Sq Ft (optional)</Label>
                      <div className="relative">
                        <Square className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="1500"
                          value={sqft}
                          onChange={(e) => setSqft(e.target.value)}
                          className="pl-10"
                          type="number"
                          data-testid="input-sqft"
                        />
                      </div>
                    </div>
                  </div>

                  {analysisType === "address" && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label>Current/Target Rent (optional)</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder="2500"
                            value={currentRent}
                            onChange={(e) => setCurrentRent(e.target.value)}
                            className="pl-10"
                            type="number"
                            data-testid="input-current-rent"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Search Radius</Label>
                        <Select value={radius} onValueChange={setRadius}>
                          <SelectTrigger data-testid="select-radius">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.5">0.5 miles</SelectItem>
                            <SelectItem value="1.0">1 mile</SelectItem>
                            <SelectItem value="2.0">2 miles</SelectItem>
                            <SelectItem value="5.0">5 miles</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full mt-6 bg-teal-500 hover:bg-teal-400 text-navy-900 font-semibold h-12"
                    disabled={loading}
                    data-testid="analyze-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="h-5 w-5 mr-2" />
                        Check Rents
                      </>
                    )}
                  </Button>
                </form>
              </Tabs>
            </CardContent>
          </Card>

          {/* Pro Feature Banner */}
          {currentTier !== "pro" && currentTier !== "admin" && (
            <Card className="mb-8 border-purple-200 bg-purple-50/50">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Calculator className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-navy-900 mb-1">
                        Compare Multiple Properties
                      </h3>
                      <p className="text-sm text-slate-600">
                        Upgrade to Pro to compare up to 5 properties side-by-side and find the best investment opportunity.
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate("/subscription")}
                    className="bg-purple-500 hover:bg-purple-400 text-white flex-shrink-0"
                    data-testid="upgrade-banner-btn"
                  >
                    Upgrade - $10/mo
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Reports */}
          <Card className="shadow-lg border-0" data-testid="recent-reports-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-teal-500" />
                  Recent Reports
                </CardTitle>
                <Link to="/reports">
                  <Button variant="ghost" size="sm" className="text-teal-600 hover:text-teal-700">
                    View All
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {loadingReports ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : recentReports.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No reports yet. Run your first analysis above!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentReports.map((report) => (
                    <Link 
                      key={report.report_id} 
                      to={`/report/${report.report_id}`}
                      className="block"
                      data-testid={`report-item-${report.report_id}`}
                    >
                      <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-navy-900 truncate">{report.address}</p>
                          <p className="text-sm text-slate-500">
                            {report.city}, {report.state} • {report.bedrooms} BR • {report.property_type.replace("_", " ")}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-data font-semibold text-navy-900">
                            ${report.estimated_rent?.toLocaleString()}/mo
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(report.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400 ml-2" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
